import { ArrowClockwiseIcon, CaretDownIcon, CaretUpIcon, GearIcon, WarningCircleIcon } from '@phosphor-icons/react'
import classNames from 'classnames'
import { useId, useState } from 'react'
import { match } from 'ts-pattern'
import { z } from 'zod'
import { SUPPORT_EMAIL } from '../../constants.ts'
import { useGetScanCancellation } from '../../hooks/scan-hooks.ts'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { Spinner } from '../spinner.tsx'
import { ResolutionCategorizationDrawer } from './resolution-categorization-drawer.tsx'
import { bucketPaletteScope } from './resolution-categorization-palette.css.ts'
import type { Categorization } from './resolution-categorization.ts'
import { ResolutionCollapsedBar } from './resolution-collapsed-bar.tsx'
import { getFunnelVariant, type FunnelVariant } from './resolution-funnel-rows.ts'
import { ResolutionFunnel } from './resolution-funnel.tsx'
import * as styles from './resolution-panel.css.ts'
import { ResolutionScopeControl } from './resolution-scope-control.tsx'
import type { ResolutionFilter, ResolutionPanelData } from './resolution-taxonomy.ts'
import { useResolutionPanel } from './use-resolution-panel.ts'

// #region Wrapper

export type ResolutionPanelProps = {
  scanId: string
  header?: React.ReactNode
  actions?: React.ReactNode
}

/**
 * The Resolution panel connected to a scan: loads the counts and drives the findings table's
 * filters. Sits inside `FilterStateStoreProvider`, whose store it reads and writes.
 */
export const ResolutionPanel: React.FC<ResolutionPanelProps> = ({ scanId, header, actions }) => {
  const {
    data,
    isLoading,
    isError,
    onRetry,
    isExpanded,
    onToggleExpanded,
    activeFilter,
    onFilterChange,
    scope,
    onScopeChange,
    categorization,
    onCategorizationChange,
    hiddenByScopeCount,
    hiddenAnalyzedByScopeCount,
    statistics,
    scale,
    quickViewId,
  } = useResolutionPanel({ scanId })
  const { hasCancellation } = useGetScanCancellation({ scanId })
  const [isEditingCategorization, setIsEditingCategorization] = useState(false)

  return (
    <ResolutionPanelPure
      data={data}
      // The panel replaces the whole header block on the findings tab, so it keeps its shell and
      // its slots on screen while the composition loads. Returning nothing here would take the
      // scan info, badge and metrics off screen once the lifecycle query flips this branch.
      bodyState={match({ isError, isLoading })
        .with({ isError: true }, () => 'error' as const)
        .with({ isLoading: true }, () => 'loading' as const)
        .otherwise(() => 'ready' as const)}
      onRetry={onRetry}
      isExpanded={isExpanded}
      onToggleExpanded={onToggleExpanded}
      header={header}
      // The panel's own controls come first, then whatever the page supplied — the scan badge sits
      // outermost, beside the collapse toggle, as the design shows.
      actions={
        <>
          <ResolutionScopeControl scope={scope} onScopeChange={onScopeChange} statistics={statistics} scale={scale} />
          <button
            type="button"
            className={styles.gearButton}
            aria-label="Categorization settings"
            aria-expanded={isEditingCategorization}
            onClick={() => setIsEditingCategorization(true)}
          >
            <GearIcon size={20} weight="bold" />
          </button>
          {actions}
          {isEditingCategorization ? (
            <ResolutionCategorizationDrawer
              categorization={categorization}
              onCategorizationChange={onCategorizationChange}
              resolution={data.resolution}
              onClose={() => setIsEditingCategorization(false)}
            />
          ) : undefined}
        </>
      }
      activeFilter={activeFilter}
      onFilterChange={onFilterChange}
      onApplyNextAction={onFilterChange}
      funnelVariant={getFunnelVariant(quickViewId)}
      isUnfiltered={!activeFilter}
      categorization={categorization}
      hiddenByScopeCount={hiddenByScopeCount}
      hiddenAnalyzedByScopeCount={hiddenAnalyzedByScopeCount}
      isAnalysisCancelled={hasCancellation}
    />
  )
}

// #endregion

// #region Pure Component

/** Whether the composition can be drawn yet. The panel's head renders in every state. */
export const ResolutionPanelBodyStateSchema = z.enum(['loading', 'error', 'ready'])
export type ResolutionPanelBodyState = z.infer<typeof ResolutionPanelBodyStateSchema>

export type ResolutionPanelPureProps = {
  data: ResolutionPanelData
  bodyState?: ResolutionPanelBodyState
  /** Reloads the counts after they failed to load. Offered from the error state's Retry button. */
  onRetry?: () => unknown
  isExpanded: boolean
  onToggleExpanded: () => unknown
  /** Scan metadata rendered at the head's start — date, "Latest scan with analysis", and the like. */
  header?: React.ReactNode
  /** Head controls that sit before the collapse toggle, such as data scope and categorization. */
  actions?: React.ReactNode
  activeFilter?: ResolutionFilter
  onFilterChange?: (filter: ResolutionFilter | undefined) => unknown
  onApplyNextAction?: (filter: ResolutionFilter) => unknown
  /** Findings removed from the data set by the active data scope, footnoted beside the totals. */
  hiddenByScopeCount?: number
  /** The analysed share of `hiddenByScopeCount`, kept in the resolved counter's denominator. */
  hiddenAnalyzedByScopeCount?: number
  /**
   * The funnel's shape, chosen from the quick view by `getFunnelVariant`. Also decides whether the
   * collapsed bar shows its analyzing and unanalyzed counters: only the All findings view holds
   * those findings, so in any other view the counters would describe findings outside it.
   */
  funnelVariant?: FunnelVariant
  isUnfiltered?: boolean
  /** How the resolution states group into the segments and colours the panel draws. */
  categorization?: Categorization
  /** The scan's analysis was cancelled, which the empty states say when nothing finished or runs. */
  isAnalysisCancelled?: boolean
}

/**
 * The Resolution panel that sits above the findings table. Expanded it shows the funnel —
 * how findings flow through Pixee; collapsed it shows a headline count and a composition
 * strip. Both states filter the table when a segment or row is selected.
 */
export const ResolutionPanelPure: React.FC<ResolutionPanelPureProps> = ({
  data,
  bodyState = 'ready',
  onRetry,
  isExpanded,
  onToggleExpanded,
  header,
  actions,
  activeFilter,
  onFilterChange,
  onApplyNextAction,
  hiddenByScopeCount = 0,
  hiddenAnalyzedByScopeCount = 0,
  funnelVariant = 'all-findings',
  isUnfiltered,
  categorization,
  isAnalysisCancelled = false,
}) => {
  const bodyId = useId()

  return (
    // The bucket colours are vars defined on this class, so it has to sit on the panel's root.
    <section className={classNames(styles.container, bucketPaletteScope)} aria-label="Resolution">
      <div className={styles.head}>
        <div className={styles.headStart}>{header}</div>
        <div className={styles.headEnd}>
          {actions}
          <button
            type="button"
            className={styles.toggleButton}
            onClick={onToggleExpanded}
            aria-expanded={isExpanded}
            aria-controls={bodyId}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? <CaretUpIcon size={22} weight="bold" /> : <CaretDownIcon size={22} weight="bold" />}
          </button>
        </div>
      </div>
      <div id={bodyId}>
        {bodyState !== 'ready' ? (
          <ResolutionPanelBodyPlaceholder bodyState={bodyState} onRetry={onRetry} />
        ) : isExpanded ? (
          <ResolutionFunnel
            data={data}
            variant={funnelVariant}
            isAnalysisCancelled={isAnalysisCancelled}
            activeFilter={activeFilter}
            onFilterChange={onFilterChange}
            hiddenByScopeCount={hiddenByScopeCount}
            categorization={categorization}
          />
        ) : (
          <ResolutionCollapsedBar
            data={data}
            activeFilter={activeFilter}
            onFilterChange={onFilterChange}
            onApplyNextAction={onApplyNextAction}
            hiddenByScopeCount={hiddenByScopeCount}
            hiddenAnalyzedByScopeCount={hiddenAnalyzedByScopeCount}
            isUnfiltered={isUnfiltered}
            isAllFindingsView={funnelVariant === 'all-findings'}
            isAnalysisCancelled={isAnalysisCancelled}
            categorization={categorization}
          />
        )}
      </div>
    </section>
  )
}

// #endregion

const ResolutionPanelBodyPlaceholder: React.FC<{ bodyState: ResolutionPanelBodyState; onRetry?: () => unknown }> = ({
  bodyState,
  onRetry,
}) =>
  bodyState === 'error' ? (
    <div className={styles.errorCard}>
      <p className={styles.errorTitle}>
        <WarningCircleIcon size={20} weight="fill" className={styles.errorIcon} />
        Couldn't load finding metrics
      </p>
      <p className={styles.errorSubtitle}>
        The findings table below is unaffected. Try again in a moment.
        <br />
        If this keeps happening, please{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`} className={styles.errorSupportLink}>
          contact Support
        </a>
        .
      </p>
      {onRetry ? (
        <GhostButton size="small" icon={<ArrowClockwiseIcon size={14} weight="light" />} onClick={onRetry}>
          Retry
        </GhostButton>
      ) : null}
    </div>
  ) : (
    <div className={styles.bodyPlaceholder}>
      <Spinner size="16" label="Loading finding metrics..." />
    </div>
  )
